import React, { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MdExpandLess,
  MdExpandMore,
  MdFullscreenExit,
  MdOutlineRoute,
} from "react-icons/md";

import StopsMap, { StopBadge } from "./StopsMap";
import { START, stopDetails, stopName } from "./routeMapStops";

const ROW =
  "flex w-full items-start gap-[10px] border-[0px] border-b-[1px] border-l-[3px] border-solid border-b-ui-border-subtle px-[12px] py-[9px] text-left font-manrope text-[12px] leading-[1.35] transition-colors disabled:cursor-default";

/**
 * The route map over the whole screen, above the route sheet: the map
 * (StopsMap, with `maps`, `start`, `path`, `fitKey`, `status`) and beside
 * it (under it on a phone, folded until opened) the start and every stop in
 * order (`stops`, with each one's `place` or null), each with its number,
 * reference, Bolt number, customer and address. A stop picked in the list
 * opens its card on the map, centred; one picked on the map is marked in the
 * list. `selection` and `onSelect(id | null, how)` are the sheet's, so both
 * maps show the same stop. Esc or Close closes it (`onClose`); the sheet
 * underneath stays as it was. `title` and `summary` head it.
 */
const RouteMapFullScreen = ({
  maps,
  title = "",
  summary = "",
  start = null,
  stops = [],
  path = null,
  fitKey = "",
  selection = null,
  onSelect,
  status = null,
  onClose,
  drops = [],
  driver = null,
  candidates = [],
  onAdd,
  removableIds = [],
  onRemove,
  busy = false,
  tools = null,
}) => {
  const titleId = useId();
  const listId = useId();
  const closeRef = useRef(null);
  const listRef = useRef(null);
  // On a phone the list folds under the map; wider, it's always open.
  const [listOpen, setListOpen] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Esc closes the map and stops there: the sheet's own Esc (on the
  // document) would close the sheet too.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onCloseRef.current?.();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  // A stop picked on the map scrolls into view in the list.
  useEffect(() => {
    if (selection?.how !== "map") return;
    listRef.current
      ?.querySelector('[aria-current="true"]')
      ?.scrollIntoView?.({ block: "nearest" });
  }, [selection]);

  const selectedId = selection?.id ?? null;
  // Picked in the list: on a phone the list folds away to show the map.
  const pick = (id) => {
    onSelect?.(id, "list");
    setListOpen(false);
  };
  const rowTone = (picked) =>
    picked
      ? "border-l-brand bg-brand-light"
      : "border-l-transparent bg-transparent hover:bg-ui-row-hover";

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-[1350] flex flex-col bg-ui-page font-manrope text-[12px] text-ui-text"
    >
      <header className="flex shrink-0 items-center gap-[11px] border-b-[1px] border-ui-border bg-ui-surface px-[14px] pb-[10px] pt-[calc(10px+env(safe-area-inset-top,0px))]">
        <span className="hidden h-[34px] w-[34px] shrink-0 place-items-center rounded-[8px] bg-brand-light text-brand sm:grid">
          <MdOutlineRoute className="h-[18px] w-[18px]" />
        </span>
        <div className="grid min-w-[0px] flex-1 gap-[2px]">
          <h2
            id={titleId}
            className="m-[0px] truncate font-jakarta text-[15px] font-extrabold leading-[1.25] tracking-[-0.01em] text-ui-text"
          >
            Route map{title ? ` · ${title}` : ""}
          </h2>
          {summary && (
            <span className="truncate text-[11px] font-medium leading-[1.35] text-ui-text-muted">
              {summary}
            </span>
          )}
        </div>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          aria-label="Close full screen"
          className="flex shrink-0 items-center gap-[6px] rounded-[8px] border-[1px] border-ui-border bg-ui-surface px-[10px] py-[6px] text-[12px] font-semibold text-ui-text hover:border-brand hover:bg-brand-light hover:text-brand"
        >
          <MdFullscreenExit className="h-[17px] w-[17px]" />
          Close
        </button>
      </header>
      <div className="flex min-h-[0px] flex-1 flex-col md:flex-row">
        <StopsMap
          maps={maps}
          start={start}
          stops={stops}
          count={stops.length}
          path={path}
          fitKey={fitKey}
          selection={selection}
          onSelect={onSelect}
          gestureHandling="greedy"
          status={status}
          drops={drops}
          driver={driver}
          candidates={candidates}
          onAdd={onAdd}
          removableIds={removableIds}
          onRemove={onRemove}
          busy={busy}
          tools={tools}
          className="min-h-[0px] flex-1 md:order-2"
        />
        <aside
          aria-label="Stops in order"
          className={`flex shrink-0 flex-col border-t-[1px] border-ui-border bg-ui-surface pb-[env(safe-area-inset-bottom,0px)] md:order-1 md:max-h-[none] md:w-[340px] md:border-r-[1px] md:border-t-0 md:pb-[0px] ${
            listOpen ? "max-h-[48%]" : ""
          }`}
        >
          <button
            type="button"
            aria-expanded={listOpen}
            aria-controls={listId}
            onClick={() => setListOpen((open) => !open)}
            className="flex w-full shrink-0 items-center gap-[8px] border-[0px] border-b-[1px] border-solid border-b-ui-border bg-ui-surface px-[14px] py-[10px] text-left text-[12.5px] font-semibold text-ui-text md:hidden"
          >
            <span className="flex-1">Stops in order ({stops.length})</span>
            <span className="flex items-center gap-[2px] text-[11.5px] text-brand">
              {listOpen ? "Hide" : "Show"}
              {listOpen ? (
                <MdExpandMore className="h-[18px] w-[18px]" />
              ) : (
                <MdExpandLess className="h-[18px] w-[18px]" />
              )}
            </span>
          </button>
          <p className="m-[0px] hidden shrink-0 border-b-[1px] border-ui-border px-[14px] py-[10px] text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted md:block">
            Stops in order ({stops.length})
          </p>
          <ol
            id={listId}
            ref={listRef}
            className={`m-[0px] min-h-[0px] flex-1 list-none overflow-y-auto overscroll-y-contain p-[0px] md:block ${
              listOpen ? "block" : "hidden"
            }`}
          >
            {start && (
              <li>
                <button
                  type="button"
                  aria-current={selectedId === START ? "true" : undefined}
                  onClick={() => pick(START)}
                  disabled={!start.place}
                  className={`${ROW} ${rowTone(selectedId === START)}`}
                >
                  <StopBadge />
                  <span className="grid min-w-[0px] gap-[1px]">
                    <span className="text-[10px] font-semibold uppercase tracking-[0.06em] text-ui-text-muted">
                      Start and end
                    </span>
                    <span className="font-bold text-ui-text">{start.name}</span>
                    <span className="text-ui-text-muted">{start.address}</span>
                  </span>
                </button>
              </li>
            )}
            {stops.map((stop) => {
              const picked = selectedId === stop.id;
              return (
                <li key={stop.id}>
                  <button
                    type="button"
                    aria-current={picked ? "true" : undefined}
                    onClick={() => pick(stop.id)}
                    disabled={!stop.place}
                    className={`${ROW} ${rowTone(picked)}`}
                  >
                    <StopBadge number={stop.number} />
                    <span className="grid min-w-[0px] gap-[1px]">
                      <span className="text-[13px] font-extrabold leading-[1.3] text-ui-text">
                        {stopName(stop)}
                      </span>
                      <span className="text-[11px] text-ui-text-muted">
                        {stopDetails(stop)}
                      </span>
                      <span className="text-ui-text">
                        {stop.customer || "No name"}
                      </span>
                      {stop.address ? (
                        <span className="text-ui-text-muted">
                          {stop.address}
                        </span>
                      ) : (
                        <span className="text-state-danger-text">
                          No delivery address: not on the map
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
      </div>
    </div>,
    document.body,
  );
};

export default RouteMapFullScreen;
